/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Params, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  Plus,
  PencilLine,
  Trash2,
  FileText,
  FileSignature
} from 'lucide-react';

import Button from '@components/atoms/ui-kit/button.tsx';
import CreateNewTemplateForm from '@components/back-office/customer/create-new-template-form';
import UpdateNewTemplateForm from '@components/back-office/customer/update-new-template-form';
import PageHeader from '@components/layout/page-header';
import Loader from '@components/atoms/loaders/loader';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';

import { useGetExportBoTemplates } from '@utils/api/back-office/customer-bo/use-get-export-bo-templates';
import { useDeleteExportBoTemplate } from '@utils/api/back-office/customer-bo/use-delete-export-bo-template';
import defaultPrompt, {
  defaultSummarizationPrompt
} from '@utils/types/defaultPrompt';
import { Template } from '@utils/types/organization/export-template';
import { BaseError } from '@utils/types/shared/base';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

type Row = {
  id: number;
  name: string;
  type: string;
  type_raw?: string | null;
  template_docx: string | null;
  template_example: string | null;
};

const BackofficeAccountPersonalization = () => {
  const { t } = useTranslation();
  const { customerId } = useParams<Params>();
  const cid = Number(customerId) || 0;
  const { getColour } = useThemeContext();

  const [templateType, setTemplateType] = useState({
    value: 'EXPORT_TEMPLATE',
    label: 'EXPORT_TEMPLATE'
  });
  const [createTemplateModal, setCreateTemplateModal] = useState(false);
  const [editTemplateModal, setEditTemplateModal] = useState(false);
  const [deleteTemplateModal, setDeleteTemplateModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    number | undefined
  >(undefined);
  const [selectedRow, setSelectedRow] = useState<Row | undefined>(undefined);
  const [newTemplates, setNewTemplates] = useState<Template[]>([]);

  const getDefaultPrompt = (type: string) => {
    return type === 'SUMMARIZATION'
      ? defaultSummarizationPrompt
      : defaultPrompt;
  };

  const {
    data: templates,
    isLoading,
    isFetching
  } = useGetExportBoTemplates(cid);
  const { mutate: deleteTemplate, isLoading: deleteIsLoading } =
    useDeleteExportBoTemplate();

  const schema = yup.object().shape({
    name: yup.string().required(t('name-is-required')),
    template_type: yup.string().required(t('type-is-required')),
    template_docx: yup.mixed().required(t('DOCX-file-is-required')),
    template_example: yup.mixed().required(t('PDF-file-is-required')),
    prompt: yup.string().optional()
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors }
  } = useForm<Template>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      template_type: templateType.value,
      template_docx: null,
      template_example: null,
      prompt: getDefaultPrompt(templateType.value)
    }
  });

  const handleAddTemplate = () => {
    setNewTemplates([
      {
        id: newTemplates.length + 1,
        name: '',
        template_docx: null,
        template_example: null,
        template_type: templateType.value,
        prompt: getDefaultPrompt(templateType.value)
      }
    ]);
    reset({
      prompt: getDefaultPrompt(templateType.value),
      name: '',
      template_type: templateType.value,
      template_docx: null,
      template_example: null
    });
    setCreateTemplateModal(false);
  };

  useEffect(() => {
    if (!createTemplateModal) setNewTemplates([]);
  }, [createTemplateModal]);

  const downloadTemplate = async (
    templateId: number,
    file: 'docx' | 'pdf',
    name: string
  ) => {
    const toastId = toast.loading(t('downloading') || 'Downloading...', {
      icon: '⬇️'
    });

    try {
      const res = await fetch(
        `/back-office/customers/${cid}/export-templates/${templateId}/download?file=${file}`,
        { credentials: 'include' }
      );

      if (res.status === 409) {
        toast.dismiss(toastId);
        toast.error(
          t('template-archived') ||
            'This file is archived and cannot be downloaded yet. Please try again later.'
        );
        return;
      }

      if (res.status === 202) {
        toast.dismiss(toastId);
        toast(
          t('template-rehydrating') ||
            'This file is being prepared (rehydrating). Try again in a few minutes.',
          { icon: '⏳' }
        );
        return;
      }

      if (!res.ok) {
        toast.dismiss(toastId);
        toast.error(t('download-failed') || 'Download failed.');
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.${file}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);

      toast.dismiss(toastId);
      toast.success(t('download-started') || 'Download started.');
    } catch {
      toast.dismiss(toastId);
      toast.error(t('download-failed') || 'Download failed.');
    }
  };

  const handleDeleteFromApi = (id: number) => {
    if (!id || deleteIsLoading) return;

    deleteTemplate(
      { customerId: cid, templateId: Number(selectedTemplateId) },
      {
        onSuccess: () => {
          toast.success(t('template-deleted'));
          setDeleteTemplateModal(false);
          setNewTemplates([]);
        },
        onError: (error: AxiosError<BaseError>) => {
          const d = error.response?.data;
          const msg =
            d?.error_description ||
            d?.detail ||
            d?.error ||
            d?.message ||
            t('an-error-occurred');
          toast.error(String(msg));
        }
      }
    );
  };

  const rows = useMemo<Row[]>(() => {
    if (!templates?.result) return [] as Row[];

    return templates.result.map<Row>((item: Template) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rawType = (item as any).template_type ?? (item as any).type ?? '';

      const typeLabel =
        String(rawType) === 'EXPORT_TEMPLATE'
          ? 'EXPORT_TEMPLATE'
          : String(rawType || t('unknown'));

      return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        id: Number((item as any).id) || 0,
        name: String(item.name ?? ''),
        type: typeLabel,
        type_raw: String(rawType || ''),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        template_docx: (item as any).template_docx ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        template_example: (item as any).template_example ?? null
      };
    });
  }, [templates?.result, t]);

  const cardBorder = getColour('hairline');

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        header: t('name'),
        field: 'name',
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('type'),
        field: 'type',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: 'EXPORT_TEMPLATE', value: 'EXPORT_TEMPLATE' },
          { label: 'SUMMARIZATION', value: 'SUMMARIZATION' }
        ],
        cell: ({ row }) => {
          const displayType =
            row.original.type === 'SUMMARIZATION'
              ? 'AI Summarization'
              : 'Export Template';
          return (
            <span
              className="inline-flex items-center rounded-full border border-hairline px-2 py-0.5 text-xs font-medium"
              style={{ color: getColour('normalText') }}
            >
              {displayType}
            </span>
          );
        }
      },
      {
        header: t('actions'),
        field: 'id',
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        enableRowCopyOnClick: false,
        cell: ({ row }) => {
          const hasDocx = row.original.template_docx;
          const hasPdf = row.original.template_example;

          return (
            <div className="flex items-center gap-2">
              {hasDocx && (
                <button
                  type="button"
                  onClick={() =>
                    downloadTemplate(
                      Number(row.original.id),
                      'docx',
                      row.original.name
                    )
                  }
                  className="inline-flex h-8 items-center justify-center gap-1 rounded-full border border-hairline bg-app-soft px-2 text-xs hover:opacity-80 transition-opacity"
                  style={{ color: getColour('normalText') }}
                >
                  <FileText size={14} />
                  <span>.docx</span>
                </button>
              )}
              {hasPdf && (
                <button
                  type="button"
                  onClick={() =>
                    downloadTemplate(
                      Number(row.original.id),
                      'pdf',
                      row.original.name
                    )
                  }
                  className="inline-flex h-8 items-center justify-center gap-1 rounded-full border border-hairline bg-app-soft px-2 text-xs hover:opacity-80 transition-opacity"
                  style={{ color: getColour('normalText') }}
                >
                  <FileSignature size={14} />
                  <span>.pdf</span>
                </button>
              )}
            </div>
          );
        }
      }
    ],
    [t, cardBorder, getColour]
  );

  const handleCellClick = (row: Row) => {
    setSelectedTemplateId(row.id);
    setSelectedRow(row);
  };

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />,
        intent: 'primary',
        run: row => {
          setSelectedTemplateId(row.id);
          setSelectedRow(row);
          setEditTemplateModal(true);
        }
      },
      {
        id: 'download-docx',
        label: t('download-docx') || 'Download DOCX',
        icon: <FileText className="h-4 w-4" />,
        run: row => {
          if (row.template_docx) {
            void downloadTemplate(Number(row.id), 'docx', row.name);
          }
        }
      },
      {
        id: 'download-pdf',
        label: t('download-pdf') || 'Download PDF',
        icon: <FileSignature className="h-4 w-4" />,
        run: row => {
          if (row.template_example) {
            void downloadTemplate(Number(row.id), 'pdf', row.name);
          }
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => {
          setSelectedTemplateId(row.id);
          setSelectedRow(row);
          setDeleteTemplateModal(true);
        }
      }
    ],
    [t]
  );

  const menuOptions = useMemo(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />
      },
      {
        id: 'download-docx',
        label: t('download-docx') || 'Download DOCX',
        icon: <FileText className="h-4 w-4" />
      },
      {
        id: 'download-pdf',
        label: t('download-pdf') || 'Download PDF',
        icon: <FileSignature className="h-4 w-4" />
      },
      { id: 'delete', label: t('delete'), icon: <Trash2 className="h-4 w-4" /> }
    ],
    [t]
  );

  const handleSetMenuOptions = (item: Row) => {
    setSelectedTemplateId(item.id);
    setSelectedRow(item);
  };

  const handleMenuOptionClick = (optionId: string) => {
    if (!selectedRow) return;
    if (optionId === 'edit') setEditTemplateModal(true);
    else if (optionId === 'download-docx' && selectedRow.template_docx)
      void downloadTemplate(Number(selectedRow.id), 'docx', selectedRow.name);
    else if (optionId === 'download-pdf' && selectedRow.template_example)
      void downloadTemplate(Number(selectedRow.id), 'pdf', selectedRow.name);
    else if (optionId === 'delete') setDeleteTemplateModal(true);
  };

  return (
    <div>
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <div className={cn('py-2 min-w-full space-y-4')}>
          <PageHeader
            title={t('templates')}
            contentClassName={'py-0 pb-4'}
            caption={t(
              'list-of-all-the-templates-including-their-name-type-and-actions'
            )}
          >
            <Button
              label={t('add-template')}
              icon={<Plus width={14} />}
              variant="primary"
              onClick={() => {
                handleAddTemplate();
                setCreateTemplateModal(true);
              }}
            />
          </PageHeader>

          <DataGridV2<Row>
            data={rows}
            columns={columns}
            actions={actions}
            enableRowSelection={false}
            enablePagination
            manualPagination
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            totalItems={templates?.total_results ?? rows.length}
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            showColumnFilters={false}
            filterMode="popover"
            height="75vh"
            dense
            grid={false}
            striped
            loading={isLoading || isFetching || !templates}
            getRowId={row => String(row.id)}
            onCellClick={handleCellClick}
            menuOptions={menuOptions}
            setMenuOptions={handleSetMenuOptions}
            onMenuOptionClick={handleMenuOptionClick}
          />
        </div>
      )}

      {createTemplateModal && (
        <CreateNewTemplateForm
          newTemplates={newTemplates}
          reset={reset}
          errors={errors}
          control={control}
          register={register}
          handleSubmit={handleSubmit}
          setCreateTemplateModal={setCreateTemplateModal}
          createTemplateModal={createTemplateModal}
          templateType={templateType}
          setTemplateType={setTemplateType}
          setValue={setValue}
          setNewTemplates={setNewTemplates}
        />
      )}

      {deleteTemplateModal && selectedTemplateId && (
        <ConfirmationModal
          isOpen={deleteTemplateModal}
          onClose={() => setDeleteTemplateModal(false)}
          onConfirm={() => handleDeleteFromApi(selectedTemplateId)}
          size="small"
          title={t('delete-template')}
          caption={t('are-you-sure-you-want-to-delete-this-template')}
          confirmLabel={deleteIsLoading ? t('deleting') : t('delete')}
          cancelLabel={t('cancel')}
          confirmDisabled={deleteIsLoading}
          destructive
          autoFocusConfirm
        />
      )}

      {editTemplateModal && (
        <UpdateNewTemplateForm
          editTemplateModal={editTemplateModal}
          setEditTemplateModal={setEditTemplateModal}
          selectedTemplateId={selectedTemplateId}
          getValues={getValues}
          reset={reset}
          errors={errors}
          control={control}
          register={register}
          handleSubmit={handleSubmit}
          templateType={templateType}
          setTemplateType={setTemplateType}
          setValue={setValue}
          setNewTemplates={setNewTemplates}
          watch={watch}
        />
      )}
    </div>
  );
};

export default BackofficeAccountPersonalization;
